import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { PlusCircle, Eye } from "react-feather";
import DataTable from "../../../../components/common/DataTable";
import PrimaryCell from "../../../../components/common/PrimaryCell";
import StatusBadge from "../../../../components/common/StatusBadge";
import DateRangeFilter, { useDateRangeFilter } from "../../components/list/DateRangeFilter";
import ListExportToolbar from "../../components/list/ListExportToolbar";
import { useListStockAdjustmentsQuery } from "../../stockApi";
import { useCanCreateAdjustment } from "../../useStockPermissions";
import { useBranchFilter } from "../../../branchFilter/useBranchFilter";
import { paths } from "../../../../routes/paths";

const TYPE_OPTIONS = [
  { value: "", label: "All" },
  { value: "INCREASE", label: "Increase" },
  { value: "DECREASE", label: "Decrease" },
];

const StockAdjustmentList = () => {
  const canCreate = useCanCreateAdjustment();
  const navigate = useNavigate();
  const { selectedBranchId, availableBranches } = useBranchFilter();
  const [adjustmentType, setAdjustmentType] = useState("");
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const { dateFrom, setDateFrom, dateTo, setDateTo } = useDateRangeFilter();

  const { data, isLoading } = useListStockAdjustmentsQuery({
    branchId: selectedBranchId ?? undefined,
    adjustmentType: adjustmentType || undefined,
    includeDeleted,
    pageSize: 100,
  });

  const filteredItems = (data?.items || []).filter((r) => {
    if (!r.adjustmentDate) return true;
    const d = dayjs(r.adjustmentDate).format("YYYY-MM-DD");
    return (!dateFrom || d >= dateFrom) && (!dateTo || d <= dateTo);
  });

  const branchCode = availableBranches.find((b) => b.branchId === selectedBranchId)?.branchCode;
  // No monetary field on this record (Increase/Decrease is a quantity
  // correction, not a value) - export/print row count only, no grand total.
  const exportColumns = [
    { header: "Adjustment No", value: (r) => r.adjustmentNo },
    { header: "Branch", value: (r) => r.branchCode },
    { header: "Type", value: (r) => (r.adjustmentType === "INCREASE" ? "Increase" : "Decrease") },
    { header: "Reason", value: (r) => r.reason },
    { header: "Date", value: (r) => (r.adjustmentDate ? new Date(r.adjustmentDate).toLocaleDateString() : "-") },
    { header: "Status", value: (r) => r.status },
  ];

  const columns = [
    {
      // No party on an adjustment - the reason is what says what it was, so it
      // leads; the Adjustment number is the link underneath (2026-09-12).
      title: "Adjustment",
      dataIndex: "adjustmentNo",
      render: (value, record) => (
        <PrimaryCell primary={record.reason} secondary={value} to={paths.stockAdjustmentDetail(record.recordGuid)} />
      ),
    },
    { title: "Branch", dataIndex: "branchCode" },
    { title: "Type", dataIndex: "adjustmentType", render: (v) => (v === "INCREASE" ? "Increase" : "Decrease") },
    { title: "Date", dataIndex: "adjustmentDate", render: (v) => (v ? new Date(v).toLocaleDateString() : "-") },
    { title: "Status", dataIndex: "status", render: (v) => <StatusBadge status={v} /> },
    {
      title: "Deleted",
      dataIndex: "isDeleted",
      render: (v) => (v ? <span className="badge badge-linedanger">Deleted</span> : "-"),
    },
    {
      title: "Actions",
      dataIndex: "actions",
      render: (_, record) => (
        <button type="button" className="btn btn-link p-2" title="View" onClick={() => navigate(paths.stockAdjustmentDetail(record.recordGuid))}>
          <Eye size={16} />
        </button>
      ),
    },
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <h4>Stock Adjustments</h4>
              <h6>Corrections to book stock with a required approval</h6>
            </div>
          </div>
          {canCreate && (
            <div className="page-btn">
              <button type="button" className="btn btn-added" onClick={() => navigate(paths.stockAdjustmentNew)}>
                <PlusCircle className="me-2" size={16} />
                New Adjustment
              </button>
            </div>
          )}
        </div>

        <div className="card table-list-card">
          <div className="card-body">
            <div className="table-top flex-wrap gap-2 justify-content-end">
              <DateRangeFilter dateFrom={dateFrom} setDateFrom={setDateFrom} dateTo={dateTo} setDateTo={setDateTo} />
              <select className="form-control form-control-sm" style={{ width: 150 }} value={adjustmentType} onChange={(e) => setAdjustmentType(e.target.value)}>
                {TYPE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <div className="form-check">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="adjustment-include-deleted"
                  checked={includeDeleted}
                  onChange={(e) => setIncludeDeleted(e.target.checked)}
                />
                <label className="form-check-label" htmlFor="adjustment-include-deleted">Show deleted</label>
              </div>
              <ListExportToolbar
                title="Stock Adjustments"
                subtitleLines={[`Branch: ${branchCode || "All"}`, `Date: ${dateFrom} to ${dateTo}`]}
                branchCode={branchCode}
                columns={exportColumns}
                rows={filteredItems}
                fileName="StockAdjustments"
                groupBy={(r) => (r.adjustmentType === "INCREASE" ? "Increase" : "Decrease")}
              />
            </div>
            <div className="table-responsive">
              <DataTable columns={columns} dataSource={filteredItems} rowKey="recordGuid" loading={isLoading} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StockAdjustmentList;
